"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RedirectPageSearchParams = exports.Origin = exports.Routes = void 0;
exports.createNewProjectRoute = createNewProjectRoute;
exports.cloneDocumentRoute = cloneDocumentRoute;
exports.cloneExampleProjectRoute = cloneExampleProjectRoute;
exports.cloneLearningFluxProjectRoute = cloneLearningFluxProjectRoute;
exports.cloneDocumentFromTemplateRoute = cloneDocumentFromTemplateRoute;
exports.forkDocumentRoute = forkDocumentRoute;
exports.externalRedirectRoute = externalRedirectRoute;
const zod_1 = require("zod");
/**
 * These routes are used in App.tsx, the main application routing, but are also used for reverse routing (aka generating
 * dynamic URLs) in the application code.
 *
 * Because both useParams() and reverse routing depend on the exact name of the captured parameters, make sure you search
 * the codebase before renaming a parameter (such as `:handle`)
 *
 * Note that some things use this collection as a whole, to try to match against pathname (e.g. <Markdown />). This
 * doesn't work for routes containing any placeholders, but is nonetheless important to be aware of before removing
 * anything from this object.
 */
exports.Routes = {
    ACCOUNT_UPGRADE: "/account/upgrade",
    CREATE_ORGANIZATION: "/organization/create",
    SIGN_UP: "/signup",
    LOG_IN: "/login",
    LOG_OUT: "/logout",
    // NOTE: for reset password routes, also see config in
    // https://console.firebase.google.com/project/graviton-mvp/authentication/emails
    RESET_PASSWORD: "/reset-password",
    RESET_PASSWORD_CALLBACK: "/reset-password/__/auth/action",
    VERIFY_EMAIL: "/verify-email",
    LANDING: "/",
    CMS_ROOT: "/p",
    GET_STARTED: "/p/get-started",
    COPILOT: "/copilot",
    BLOG: "/blog",
    SEARCH: "/search",
    SITEMAP: "/sitemap",
    SITEMAP_CATEGORY: "/sitemap/:category",
    SITEMAP_SUBCATEGORY: "/sitemap/:category/:subcategory",
    CREATE_DOCUMENT: "/projects/create",
    CREATE_DOCUMENT_FROM_TEMPLATE: "/projects/createDocumentFromTemplate",
    FORK_DOCUMENT: "/projects/forkProject",
    CLONE_DOCUMENT: "/projects/cloneProject",
    CLONE_EXAMPLE_DOCUMENT: "/projects/cloneExampleProject",
    CLONE_LEARNING_DOCUMENT: "/projects/cloneLearningProject",
    /**
     * We use this url to handle any external redirection
     */
    REDIRECT: "/redirect",
    INTERNAL_DYNAMIC: "/internal/:section?/:subsection?",
    /**
     * @deprecated Does not cope with full complexity of document paths - use getDocumentUrl()
     */
    PROJECT_BY_UID: "/projects/:documentUid",
    /**
     * @deprecated Does not cope with full complexity of document paths - use getDocumentUrl()
     */
    PROJECT_BY_SLUG: "/:handle/:documentSlug",
    /**
     * @deprecated Does not cope with full complexity of document paths - use getDocumentUrl()
     */
    LOCAL_COMPONENT_BY_SLUG: "/:handle/:baseSlug/:componentSlug", // Try to avoid using this many wildcards in a row in future
    // NOTE: this route is handled by cloudflare in production. It doesn't work in localhost.
    STORYBOOK: "/storybook",
    /** @deprecated use LOG_IN except in BC code */
    SIGN_IN: "/signin",
    /** @deprecated use LOG_OUT except in BC code */
    SIGN_OUT: "/signout",
};
var Origin;
(function (Origin) {
    Origin["Menu"] = "Menu";
    Origin["Hotkey"] = "Hotkey";
    Origin["Profile"] = "Profile";
    Origin["NavBar"] = "NavBar";
    Origin["AutoLayoutAnnouncement"] = "AutoLayoutAnnouncement";
})(Origin || (exports.Origin = Origin = {}));
function createNewProjectRoute(origin, organizationUid, enterpriseUid) {
    const searchParams = new URLSearchParams();
    searchParams.set("origin", origin);
    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }
    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }
    return `${exports.Routes.CREATE_DOCUMENT}?${searchParams.toString()}`;
}
function cloneDocumentRoute(documentUid, origin, organizationUid, enterpriseUid) {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);
    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }
    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }
    return `${exports.Routes.CLONE_DOCUMENT}?${searchParams.toString()}`;
}
function cloneExampleProjectRoute(documentUid, origin, editorView) {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);
    if (editorView) {
        searchParams.set("editorMode", editorView);
    }
    return `${exports.Routes.CLONE_EXAMPLE_DOCUMENT}?${searchParams.toString()}`;
}
function cloneLearningFluxProjectRoute(documentUid, origin, editorView) {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);
    if (editorView) {
        searchParams.set("editorMode", editorView);
    }
    return `${exports.Routes.CLONE_LEARNING_DOCUMENT}?${searchParams.toString()}`;
}
function cloneDocumentFromTemplateRoute(documentUid, origin, organizationUid, enterpriseUid) {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    if (origin) {
        searchParams.set("origin", origin);
    }
    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }
    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }
    return `${exports.Routes.CREATE_DOCUMENT_FROM_TEMPLATE}?${searchParams.toString()}`;
}
function forkDocumentRoute(documentUid, origin, organizationUid, enterpriseUid) {
    const searchParams = new URLSearchParams();
    searchParams.set("documentUid", documentUid);
    searchParams.set("origin", origin);
    if (organizationUid) {
        searchParams.set("organizationUid", organizationUid);
    }
    if (enterpriseUid) {
        searchParams.set("enterpriseUid", enterpriseUid);
    }
    return `${exports.Routes.FORK_DOCUMENT}?${searchParams.toString()}`;
}
const RedirectPageSearchParamsForDataSheet = zod_1.z.object({
    type: zod_1.z.literal("datasheet"),
    projectUid: zod_1.z.string(),
});
/**
 * Search parameters for this page, can extend it via discriminative union from zod
 */
exports.RedirectPageSearchParams = RedirectPageSearchParamsForDataSheet;
function externalRedirectRoute(params) {
    const searchParams = new URLSearchParams();
    searchParams.set("type", params.type);
    if (params.type === "datasheet") {
        searchParams.set("projectUid", params.projectUid);
    }
    return `${exports.Routes.REDIRECT}?${searchParams.toString()}`;
}
//# sourceMappingURL=routes.js.map